import { Pencil, X } from 'lucide-react';
import { useState, type Ref } from 'react';

import { useFocusReturn } from './use-focus-return';

const iconButton = { width: 24, height: 24, padding: 0, justifyContent: 'center' } as const;

interface NoteActionsProps {
  onEdit: () => void;
  onDelete: () => void;
  busy: boolean;
  /** Lets the owner return focus to Edit once an editor closes. */
  editButtonRef?: Ref<HTMLButtonElement>;
}

/**
 * Edit and delete buttons for one note. Delete asks "Delete?" before it acts:
 * focus moves to the confirm button, and back to Delete if the person says No.
 */
export function NoteActions({ onEdit, onDelete, busy, editButtonRef }: NoteActionsProps) {
  const [confirming, setConfirming] = useState(false);
  const deleteButton = useFocusReturn();

  if (confirming) {
    return (
      <span
        role="group"
        aria-label="Confirm delete"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          fontSize: 10.5,
          flexShrink: 0,
          marginLeft: 'auto',
        }}
      >
        <span style={{ color: 'var(--nb-risk)', fontWeight: 700 }}>Delete?</span>
        <button
          type="button"
          className="nb-btn nb-tap"
          disabled={busy}
          onClick={onDelete}
          autoFocus
          style={{
            height: 22,
            fontSize: 10,
            padding: '0 7px',
            color: 'var(--nb-risk)',
            borderColor: 'var(--nb-risk)',
          }}
        >
          Yes, delete
        </button>
        <button
          type="button"
          className="nb-btn nb-btn-ghost nb-tap"
          onClick={() => {
            setConfirming(false);
            deleteButton.request();
          }}
          style={{ height: 22, fontSize: 10, padding: '0 7px' }}
        >
          No
        </button>
      </span>
    );
  }

  return (
    <span style={{ display: 'inline-flex', gap: 2, flexShrink: 0, marginLeft: 'auto' }}>
      <button
        type="button"
        ref={editButtonRef}
        className="nb-btn nb-btn-ghost nb-tap"
        aria-label="Edit note"
        title="Edit"
        onClick={onEdit}
        style={iconButton}
      >
        <Pencil size={11} aria-hidden="true" />
      </button>
      <button
        type="button"
        ref={deleteButton.ref}
        className="nb-btn nb-btn-ghost nb-tap"
        aria-label="Delete note"
        title="Delete"
        onClick={() => setConfirming(true)}
        style={iconButton}
      >
        <X size={12} aria-hidden="true" />
      </button>
    </span>
  );
}
